// Degustação ativa: o acesso ao ranking ao vivo (monitor e Modo TV) e os quadros criados, um card por posto.
// Mesma linguagem do convite (OfferPitch): violeta #7869DF/#4433B4 + verde #79CF62, cantos redondos, sombra suave, ícones.
import React from 'react';
import { Box, Button, Chip, Link, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import OpacityIcon from '@mui/icons-material/Opacity';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TvIcon from '@mui/icons-material/Tv';
import { __t } from '../../i18n/t';
import GradientButton from '../../components/AttendantGoalOfferCard/GradientButton';
import { goalLevelLabel, goalTypeLabel, goalValueShort } from '../../components/AttendantGoalOfferCard/offerCopy';
import { GoalLevel, OfferStationView, OfferTargetType } from '../../models/attendantGoalOffer';
import { AttendantGoalType } from '../../models/attendantGoalType';
import { monitorPath } from '../attendantGoalBoardMonitor/monitorLink';
import { COLORS, openInNewTab } from '../dashboards/shared';
import { BoardRow, boardRows, firstStationBoards, stationBoards } from './boards';
import PlaceLink from './PlaceLink';

const VIOLET = '#7869DF';
const VIOLET_DARK = '#4433B4';
const GREEN_DARK = '#275030';
const INK = '#232323';
const MUTED = '#5b6474';
const BADGE_GRADIENT = `linear-gradient(135deg, ${VIOLET}, #a855f7)`;

const PADDING_X = { xs: 1.75, sm: 2.25 };
const LEVEL_CHIP_SX = { fontWeight: 700, bgcolor: 'rgba(120,105,223,0.10)', color: VIOLET_DARK };
const BANNER_NOTE_SX = { fontSize: 12.5, color: MUTED };

// Um ícone e uma cor por tipo de meta: quantidade (violeta), fidelização pelo app (verde), volume em litros (azul).
type TypeStyle = { Icon: React.ElementType; color: string; bg: string };
const TYPE_STYLE: Record<OfferTargetType, TypeStyle> = {
  [AttendantGoalType.QUANTITY]: { Icon: LocalGasStationIcon, color: VIOLET, bg: 'rgba(120,105,223,0.12)' },
  [AttendantGoalType.PERCENTAGE_LOYALTY]: { Icon: PhoneIphoneIcon, color: '#3f9a2b', bg: 'rgba(121,207,98,0.20)' },
  [AttendantGoalType.VOLUME]: { Icon: OpacityIcon, color: COLORS.blue, bg: 'rgba(59,130,246,0.12)' },
};
// Quadro sem alvo conhecido (ou de outro tipo): ícone neutro.
const FALLBACK_STYLE: TypeStyle = { Icon: LeaderboardIcon, color: VIOLET, bg: 'rgba(120,105,223,0.12)' };

/**
 * Nível e valor da meta do quadro. Celular: lado a lado, embaixo do nome. Tela larga: duas colunas fixas, alinhadas entre as linhas.
 * (Componente à parte: o plugin de i18n embrulha em __t() todo literal dentro de `cond && <X sx=.../>`, inclusive valor de CSS.)
 */
const LevelAndValue = ({ type, level, value }: { type: OfferTargetType; level: GoalLevel | null; value: number | null }) => (
  <Box sx={{
    gridColumn: { xs: '2', sm: 'auto' }, display: { xs: 'flex', sm: 'grid' }, gridTemplateColumns: { sm: '112px 190px' },
    alignItems: 'center', columnGap: 1.5, gap: { xs: 1 }, flexWrap: 'wrap',
  }}>
    <Box sx={{ justifySelf: 'start' }}>
      {level && <Chip size="small" label={goalLevelLabel(level)} sx={LEVEL_CHIP_SX} />}
    </Box>
    <Typography sx={{ fontSize: 14, fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums', textAlign: { sm: 'right' } }}>
      {value === null ? '' : goalValueShort(type, value)}
    </Typography>
  </Box>
);

const BoardLine = ({ row }: { row: BoardRow }) => {
  const { board, type, level, value } = row;
  const { Icon, color, bg } = (type && TYPE_STYLE[type as OfferTargetType]) || FALLBACK_STYLE;
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: { xs: '40px minmax(0, 1fr)', sm: '40px minmax(0, 1fr) auto' },
      columnGap: 1.5, rowGap: 0.75, alignItems: 'center', px: PADDING_X, py: 1.5, borderTop: '1px solid rgba(35,35,35,0.06)',
    }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon sx={{ color, fontSize: 22 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: INK, lineHeight: 1.25 }}>{goalTypeLabel(type)}</Typography>
        {/* Nome do quadro como veio (quadros antigos começam com "Degustação · "). */}
        <Link
          href={`#/attendant-goal-boards/${board.id}/show`}
          target="_blank"
          rel="noopener"
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%', fontSize: 12.5, color: MUTED }}
        >
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{board.name}</Box>
          <OpenInNewIcon sx={{ fontSize: 13, flexShrink: 0 }} />
        </Link>
      </Box>
      <LevelAndValue type={type as OfferTargetType} level={level} value={value} />
    </Box>
  );
};

const StationMonitorButton = ({ placeId, boardIds }: { placeId: string; boardIds: string[] }) => (
  <Button
    size="small"
    startIcon={<TvIcon />}
    onClick={() => openInNewTab(monitorPath(placeId, boardIds))}
    sx={{ textTransform: 'none', fontWeight: 700, color: VIOLET_DARK }}
  >
    {__t('Ver no monitor')}
  </Button>
);

const StationCard = ({ station, canMonitor }: { station: OfferStationView; canMonitor: boolean }) => {
  const rows = boardRows(station);
  return (
    <Box sx={{ borderRadius: 4, bgcolor: '#fff', border: '1px solid rgba(120,105,223,0.16)', boxShadow: '0 6px 20px rgba(68,51,180,0.07)', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: PADDING_X, py: 1.25, bgcolor: 'rgba(120,105,223,0.06)' }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 2, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', boxShadow: '0 3px 10px rgba(35,35,35,0.08)' }}>
          <StorefrontIcon sx={{ fontSize: 19, color: VIOLET }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: '1 1 160px' }}>
          <PlaceLink placeId={station.placeId} label={station.placeName} />
        </Box>
        {canMonitor && <StationMonitorButton placeId={station.placeId} boardIds={rows.map((r) => r.board.id)} />}
      </Box>
      {rows.map((row) => <BoardLine key={row.board.id} row={row} />)}
    </Box>
  );
};

/**
 * Faixa de destaque com os dois caminhos para o ranking ao vivo: monitor e Modo TV. Os botões abrem um posto só (o monitor mostra
 * um posto por vez): com mais de um posto, `note` diz qual abre e onde estão os outros.
 */
const MonitorBanner = ({ onOpen, note }: { onOpen: (tv: boolean) => void; note?: string }) => (
  <Box sx={{
    display: 'flex', flexDirection: 'column', gap: 1.75, borderRadius: 4, p: { xs: 2, sm: 2.5 },
    background: 'linear-gradient(135deg, rgba(120,105,223,0.10), rgba(121,207,98,0.14))', border: '1px solid rgba(120,105,223,0.20)',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ width: 44, height: 44, borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: BADGE_GRADIENT, boxShadow: '0 8px 18px rgba(120,105,223,0.35)' }}>
        <LeaderboardIcon sx={{ color: '#fff' }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK, lineHeight: 1.25 }}>{__t('Ranking ao vivo da sua equipe')}</Typography>
        <Typography sx={{ fontSize: 13, color: GREEN_DARK, mt: 0.25 }}>
          {__t('Veja quem rende mais em cada posto. Os quadros da degustação já estão no monitor.')}
        </Typography>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
      <GradientButton
        startIcon={<LeaderboardIcon />}
        endIcon={<ArrowForwardIcon />}
        onClick={() => onOpen(false)}
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        {__t('Abrir o monitor ao vivo')}
      </GradientButton>
      <Button
        variant="outlined"
        startIcon={<TvIcon />}
        onClick={() => onOpen(true)}
        sx={{
          width: { xs: '100%', sm: 'auto' }, minHeight: 48, px: 2.5, borderRadius: 3, textTransform: 'none', fontSize: 15, fontWeight: 800,
          color: VIOLET_DARK, borderColor: VIOLET, bgcolor: 'rgba(255,255,255,0.7)', '&:hover': { borderColor: VIOLET_DARK, bgcolor: '#fff' },
        }}
      >
        {__t('Abrir no Modo TV')}
      </Button>
    </Box>
    {note && <Typography sx={BANNER_NOTE_SX}>{note}</Typography>}
  </Box>
);

/** `stations`: as do convite ativo; só entram os que têm quadro. `canMonitor`: o usuário tem a permissão do monitor. */
const ActiveBoards = ({ stations, canMonitor }: { stations: OfferStationView[]; canMonitor: boolean }) => {
  const withBoards = stations.filter((s) => stationBoards(s).length > 0);
  const first = firstStationBoards(withBoards);
  if (!first) return null;
  const openMonitor = (tv: boolean) => openInNewTab(monitorPath(first.placeId, first.boardIds, { tv }));
  const firstName = withBoards.find((s) => s.placeId === first.placeId)?.placeName ?? '';
  const note = withBoards.length > 1
    ? __t('Abre %{posto}. Os outros postos têm o atalho "Ver no monitor" no próprio card.', { posto: firstName })
    : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {canMonitor && <MonitorBanner onOpen={openMonitor} note={note} />}

      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED, mb: -0.5 }}>
        {__t('Quadros da degustação')}
      </Typography>
      {withBoards.map((s) => <StationCard key={s.placeId} station={s} canMonitor={canMonitor} />)}
    </Box>
  );
};

export default ActiveBoards;
